
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {DrawingStorageChartsResponse} from './DrawingStorageChartsResponse';

/**
 * DrawingStorageChartsRequest struct
 */
class DrawingStorageChartsRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!number=} minVersion
   * @param {!number=} maxCount
   * @constructor
   */
  constructor (parent = new Request(), minVersion = new UInt64(0, 0), maxCount = 100) {
    super()
    super.copy(parent)
    this.minVersion = minVersion
    this.maxCount = maxCount
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DrawingStorageChartsRequest}
   * @param {!DrawingStorageChartsRequest} other Other struct
   * @returns {!DrawingStorageChartsRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.minVersion != null) {
      this.minVersion = UInt64.fromNumber(other.minVersion)
    } else {
      this.minVersion = null
    }
    if (other.maxCount != null) {
      this.maxCount = other.maxCount
    } else {
      this.maxCount = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DrawingStorageChartsRequest}
   * @returns {!DrawingStorageChartsRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DrawingStorageChartsRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DrawingStorageChartsRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DrawingStorageChartsRequest}
   * @param {!DrawingStorageChartsRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DrawingStorageChartsRequest)) {
      throw new TypeError('Instance of DrawingStorageChartsRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DrawingStorageChartsRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      minVersion: ((this.minVersion != null) ? this.minVersion.toNumber() : null),
      maxCount: ((this.maxCount != null) ? this.maxCount : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DrawingStorageChartsRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DrawingStorageChartsRequest} other Object value
   * @returns {!DrawingStorageChartsRequest} Created struct
   */
  static fromObject (other) {
    return new DrawingStorageChartsRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DrawingStorageChartsRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DrawingStorageChartsRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DrawingStorageChartsRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10020
  }
}

export { DrawingStorageChartsRequest };

/**
 * Fast Binary Encoding DrawingStorageChartsRequest field model
 */
class FieldModelDrawingStorageChartsRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.minVersion = new fbe.FieldModelUInt64(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.maxCount = new fbe.FieldModelUInt32(buffer, this.minVersion.fbeOffset + this.minVersion.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.minVersion.fbeSize + this.maxCount.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.minVersion.fbeExtra + this.maxCount.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDrawingStorageChartsRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10020
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.minVersion.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.minVersion.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.minVersion.fbeSize

    if ((fbeCurrentSize + this.maxCount.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.maxCount.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.maxCount.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @param {!DrawingStorageChartsRequest} fbeValue Default value, defaults is new DrawingStorageChartsRequest()
   * @returns {!DrawingStorageChartsRequest} DrawingStorageChartsRequest value
   */
  get (fbeValue = new DrawingStorageChartsRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @param {!DrawingStorageChartsRequest} fbeValue DrawingStorageChartsRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.minVersion.fbeSize) <= fbeStructSize) {
      fbeValue.minVersion = this.minVersion.get()
    } else {
      fbeValue.minVersion = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.minVersion.fbeSize

    if ((fbeCurrentSize + this.maxCount.fbeSize) <= fbeStructSize) {
      fbeValue.maxCount = this.maxCount.get(100)
    } else {
      fbeValue.maxCount = 100
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.maxCount.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @param {!DrawingStorageChartsRequest} fbeValue DrawingStorageChartsRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDrawingStorageChartsRequest}
   * @param {DrawingStorageChartsRequest} fbeValue DrawingStorageChartsRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.minVersion.set(fbeValue.minVersion)
    this.maxCount.set(fbeValue.maxCount)
  }
}

export { FieldModelDrawingStorageChartsRequest };

/**
 * Fast Binary Encoding DrawingStorageChartsRequest model
 */
class DrawingStorageChartsRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDrawingStorageChartsRequest(this.buffer, 4)
  }

  /**
   * Get the DrawingStorageChartsRequest model
   * @this {!DrawingStorageChartsRequestModel}
   * @returns {!FieldModelDrawingStorageChartsRequest} model DrawingStorageChartsRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DrawingStorageChartsRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DrawingStorageChartsRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DrawingStorageChartsRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DrawingStorageChartsRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDrawingStorageChartsRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DrawingStorageChartsRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DrawingStorageChartsRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DrawingStorageChartsRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DrawingStorageChartsRequestModel}
   * @param {!DrawingStorageChartsRequest} value DrawingStorageChartsRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DrawingStorageChartsRequestModel}
   * @param {!DrawingStorageChartsRequest} value DrawingStorageChartsRequest value, defaults is new DrawingStorageChartsRequest()
   * @return {!object} Deserialized DrawingStorageChartsRequest value and its size
   */
  deserialize (value = new DrawingStorageChartsRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DrawingStorageChartsRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DrawingStorageChartsRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DrawingStorageChartsRequestModel}
   * @param {!number} prev Previous DrawingStorageChartsRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DrawingStorageChartsRequestModel };
DrawingStorageChartsRequest.__has_response = true;
DrawingStorageChartsRequest.__response_class = DrawingStorageChartsResponse;
